// Pure decisions behind the /dailys hub's duplicate recovery and its resumed-tab
// re-dating. The hub wires these to React state; everything that decides what
// gets written lives here so it can be tested without rendering the hub.
import {canEditOwnRecord} from '../lib/dailyReportsApi.js';
import type {DailyReportAuthState} from '../lib/dailyReportsApi.js';
import type {DailyRecord, ExistingDailyReport} from '../lib/dailyDuplicateCheck.js';

export type DuplicateHit = {
  table: string;
  record: DailyRecord;
  existing: ExistingDailyReport;
};

export function hasPhotos(record: DailyRecord): boolean {
  return Array.isArray(record.photos) && record.photos.length > 0;
}

export function isOwnReport(editor: DailyReportAuthState, hit: DuplicateHit): boolean {
  const uid = editor ? editor.user?.id : undefined;
  return !!uid && hit.existing.owner_profile_id === uid;
}

// A blocked group may be written onto its existing report only when that report
// is the caller's own, still editable by them, and the group carries no photos
// (an edit cannot attach them). Someone else's report is opened, never
// overwritten from the entry form.
export function canUpdateHit(editor: DailyReportAuthState, hit: DuplicateHit): boolean {
  return isOwnReport(editor, hit) && canEditOwnRecord(editor, hit.existing) && !hasPhotos(hit.record);
}

export function canUpdateDuplicates(editor: DailyReportAuthState, hits: readonly DuplicateHit[]): boolean {
  return hits.length > 0 && hits.every((h) => canUpdateHit(editor, h));
}

export type DuplicateDecision =
  // Nothing collides: insert everything.
  | {kind: 'insert'}
  // Show the notice for these collisions; write nothing.
  | {kind: 'block'; hits: DuplicateHit[]}
  // The confirmation no longer matches what the current form collides with.
  | {kind: 'stale'}
  // Write these groups onto their existing reports, then insert the rest.
  | {kind: 'update'; hits: DuplicateHit[]};

// `hits` are the collisions of the CURRENT, validated form, found just now.
// `confirmedIds` are the existing-report ids the user agreed to update, or null
// for a plain submit. An update happens only when the user confirmed exactly
// the reports the current form collides with and every one is updatable, so a
// confirmation can never be applied to answers, a date or a report it was not
// given for.
export function decideDuplicates(
  editor: DailyReportAuthState,
  hits: readonly DuplicateHit[],
  confirmedIds: ReadonlySet<string> | null,
): DuplicateDecision {
  const ids = hits.map((h) => h.existing.id);
  if (!confirmedIds) return hits.length === 0 ? {kind: 'insert'} : {kind: 'block', hits: [...hits]};
  const exact = ids.length === confirmedIds.size && ids.every((id) => confirmedIds.has(id));
  if (!exact) return hits.length === 0 ? {kind: 'stale'} : {kind: 'block', hits: [...hits]};
  if (!canUpdateDuplicates(editor, hits)) return {kind: 'block', hits: [...hits]};
  return {kind: 'update', hits: [...hits]};
}

function groupLabel(record: DailyRecord): string {
  return String(record.batch_label || record.herd || record.flock || record.date || '');
}

// The outcome copy when an update-then-insert run stops part way. Every write
// that landed is named, so a retry starts from an accurate picture; the form
// itself is left as it was.
export function partialFailureMessage(
  updated: readonly DuplicateHit[],
  failure: string,
  stage: 'update' | 'insert',
  failed?: DuplicateHit,
) {
  const done = updated.map((h) => groupLabel(h.record)).join(', ');
  const head = done ? `Updated your existing ${done} report${updated.length > 1 ? 's' : ''}. ` : '';
  if (stage === 'update') {
    const which = failed ? `your existing ${groupLabel(failed.record)} report` : 'the next report';
    return `${head}Could not update ${which}: ${failure}. Nothing else was submitted.`;
  }
  return `${head}The other groups were not saved: ${failure}. Submit again to send them.`;
}

// Fields that are not the reporter's answers: the date (handled separately),
// the pinned submitter and, on the egg form, the four group names that are
// filled from configuration.
const NON_ANSWER_FIELDS = new Set(['date', 'teamMember', 'g1n', 'g2n', 'g3n', 'g4n']);

// Whether a form still holds nothing the reporter entered: every answer equals
// the form's fresh copy (as set up at mount or by Submit Another). Extra groups
// or photos make a form a draft.
export function isPristineForm(
  form: Record<string, unknown>,
  fresh: Record<string, unknown>,
  {extraGroups = 0, photos = 0}: {extraGroups?: number; photos?: number} = {},
): boolean {
  if (extraGroups > 0 || photos > 0) return false;
  const keys = new Set([...Object.keys(form), ...Object.keys(fresh)]);
  for (const k of keys) {
    if (NON_ANSWER_FIELDS.has(k)) continue;
    const a = form[k];
    const b = fresh[k];
    if (
      (Array.isArray(a) && a.length === 0 && (b === undefined || b === null || (Array.isArray(b) && b.length === 0))) ||
      (Array.isArray(b) && b.length === 0 && (a === undefined || a === null || (Array.isArray(a) && a.length === 0)))
    ) {
      continue;
    }
    if (JSON.stringify(a ?? null) !== JSON.stringify(b ?? null)) return false;
  }
  return true;
}

// On returning to a tab on a new day: advance only a pristine form whose date is
// still the automatic one. A filled draft or a chosen date is kept as is; the
// form offers Use Today instead.
export function shouldAdvanceDate(
  form: Record<string, unknown>,
  previousAutoDate: string | undefined,
  pristine: boolean,
): boolean {
  return pristine && !!previousAutoDate && form.date === previousAutoDate;
}
